import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, SearchIcon, XIcon } from 'lucide-react';
import type { PlayerCri, TeamCode } from '../../types/neuro';
import { neuroTeams } from '../../data/neuroPlayers';
import { criLabel, criTone, formatMeta, isLimitedSample, toneClasses } from '../../utils/neuro';
import { LimitedBadge, PlayerAvatar } from './NeuroPrimitives';
interface AddPlayersModalProps {
  team: TeamCode;
  candidates: PlayerCri[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}
export const AddPlayersModal = ({
  team,
  candidates,
  onClose,
  onConfirm
}: AddPlayersModalProps) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const meta = neuroTeams[team];
  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);
  const filtered = candidates.filter(p => p.name.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  return <div className="neuro-font fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-457" data-mp="2a01jupmzvn44">
      <div className="absolute inset-0 bg-[#111827]/40 neuro-fade-in" onClick={onClose} aria-hidden="true" data-id="element-458" data-mp="29xouvsitkchk" />
      <div role="dialog" aria-modal="true" aria-labelledby="add-players-title" className="relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-[#E5E7EB] bg-white shadow-xl neuro-modal-in" data-id="element-459" data-mp="2012b7728brh4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 pt-5" data-id="element-460" data-mp="3syfzfwuuc2hj">
          <div data-id="element-461" data-mp="3swcy4go8mmxi">
            <h2 id="add-players-title" className="text-lg font-bold text-[#111827]" data-id="element-462" data-mp="3nzw9zmh1g8kf">
              Add players
            </h2>
            <p className={`mt-0.5 flex items-center gap-1.5 text-sm font-medium ${meta.textClass}`} data-id="element-463" data-mp="3izrv20vs3lwb">
              <span className={`w-2 h-2 rounded-full ${meta.dotClass}`} aria-hidden="true" data-id="element-464" data-mp="3ezzsq5wzabze" />
              {meta.name}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" data-id="element-465" data-mp="flnoszguik2a">
            <XIcon className="w-5 h-5" strokeWidth={1.75} data-id="element-466" data-mp="1s66tj3t5fed" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4" data-id="element-467" data-mp="yc5eswaan1jy">
          <label className="relative block" data-id="element-468" data-mp="tjc9a6703p6t">
            <span className="sr-only" data-id="element-469" data-mp="tiaxu0m2p0ok">Search players</span>
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" strokeWidth={1.75} aria-hidden="true" data-id="element-470" data-mp="2c742527egyq2" />
            <input ref={searchRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name" className="w-full rounded-lg border border-[#D1D5DB] bg-white py-2 pl-9 pr-3 text-sm text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-id="element-471" data-mp="2l6r2dkoe7yhj" />
          </label>
        </div>

        {/* List */}
        <div className="mt-3 flex-1 overflow-y-auto px-2 pb-2" data-id="element-472" data-mp="34xdp82gpfeo2">
          {candidates.length === 0 ? <p className="px-3 py-10 text-center text-sm text-[#6B7280]" data-id="element-473" data-mp="3dvpoesvmzks4">
              All analyzed players are already shown.
            </p> : filtered.length === 0 ? <p className="px-3 py-10 text-center text-sm text-[#6B7280]" data-id="element-474" data-mp="3isobwsksothc">
              No players match “{query}”.
            </p> : <ul className="space-y-1" aria-label="Available players" data-id="element-475" data-mp="309qsk9ae3mrj">
              {filtered.map(player => {
            const isChecked = selected.includes(player.player_id);
            const tone = criTone(player.cri_match);
            return <li key={player.player_id} data-id="element-476" data-mp="2m97sboc61kzx">
                    <label className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors duration-150 ${isChecked ? 'bg-[#EFF6FF]' : 'hover:bg-[#F9FAFB]'}`} data-id="element-477" data-mp="2m9zbu0shns9u">
                      <input type="checkbox" checked={isChecked} onChange={() => toggle(player.player_id)} className="sr-only peer" data-id="element-478" data-mp="2r8133902ixwe" />
                      <span className={`w-5 h-5 flex-shrink-0 rounded border flex items-center justify-center peer-focus-visible:ring-2 peer-focus-visible:ring-[#2563EB] peer-focus-visible:ring-offset-1 ${isChecked ? 'bg-[#2563EB] border-[#2563EB]' : 'bg-white border-[#D1D5DB]'}`} aria-hidden="true" data-id="element-479" data-mp="6k64hwzoyg1g">
                        {isChecked && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} data-id="element-480" data-mp="wrkcykdwtbiz" />}
                      </span>
                      <PlayerAvatar player={player} size={40} data-id="element-481" data-mp="11q3zon0burce" />
                      <span className="min-w-0 flex-1" data-id="element-482" data-mp="o00fe1l0d7pl">
                        <span className="flex items-center gap-2" data-id="element-483" data-mp="nzicim3ubv8p">
                          <span className="text-sm font-semibold text-[#111827] truncate" data-id="element-484" data-mp="s0alde8svy4g">{player.name}</span>
                          <span className="text-xs text-[#6B7280]" data-id="element-485" data-mp="1fq4y7vt4dmop">{player.position}</span>
                          {isLimitedSample(player) && <LimitedBadge data-id="element-486" data-mp="1kiy1ef8xwmyo" />}
                        </span>
                        <span className="block text-xs text-[#6B7280] mt-0.5" data-id="element-487" data-mp="1uclukxh1w8x3">{formatMeta(player)}</span>
                      </span>
                      <span className="text-right" data-id="element-488" data-mp="1q9xjej4v6pwy">
                        <span className={`block text-lg leading-none font-semibold tabular-nums ${toneClasses[tone].text}`} data-id="element-489" data-mp="1lc5r3o0b51s8">
                          {player.cri_match}
                        </span>
                        <span className={`text-[11px] ${toneClasses[tone].text}`} data-id="element-490" data-mp="1bf11fe796o6o">CRI · {criLabel[tone]}</span>
                      </span>
                    </label>
                  </li>;
          })}
            </ul>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-[#E5E7EB] px-5 py-4" data-id="element-491" data-mp="xnwcb61iheal">
          <span className="text-xs text-[#6B7280]" data-id="element-492" data-mp="sot3daieunxc">
            {selected.length > 0 ? `${selected.length} selected` : 'Select one or more players'}
          </span>
          <div className="flex gap-2" data-id="element-493" data-mp="nrj83e8cbi1l">
            <button type="button" onClick={onClose} className="rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-494" data-mp="dm1h6gduheg2">
              Cancel
            </button>
            <button type="button" disabled={selected.length === 0} onClick={() => onConfirm(selected)} className="rounded-lg bg-[#2563EB] px-3 py-2 text-sm font-medium text-white hover:bg-[#1D4ED8] disabled:bg-[#93C5FD] disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 whitespace-nowrap" data-id="element-495" data-mp="3r067x72dv2fv">
              {selected.length > 1 ? `Add ${selected.length} players` : 'Add player'}
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default AddPlayersModal;